// 📁 src/services/responseHandler.js

import { triggerSessionTimeout } from '../common/sessionTimeoutModal';

/**
 * Simple Response Handler for FileSage Custom API Format
 * Handles API response format: { status, status_code, message, data, request_id }
 * 
 * @param {Promise|Object} responsePromise - API response promise from callApi
 * @returns {Object} - { success: boolean, data: any, error: string|null, message: string }
 */
export const responseHandler = async (responsePromise) => {
  try {
    // Await the promise from callApi, which returns the parsed JSON or throws an error
    const apiResponse = await responsePromise;
    
    // apiResponse is now the direct JSON object from the server, like { status: "Success", ... }
    // or null in case of a 204 No Content response.
    if (!apiResponse) {
        return { success: true, status: "Success", status_code: 204, message: "Operation successful (No Content)", data: null };
    }

    // Check if the API response indicates success
    const isSuccess = apiResponse.status === "Success" || apiResponse.status_code == 200;
    
    // Return the exact API response with an added success field
    return {
      ...apiResponse,
      success: isSuccess
    };

  } catch (error) {
    console.error('Response handler caught an error:', error);
    
    // Only 401 should trigger session-timeout UX.
    if (
      (error.status === 401 || error.status_code === 401) &&
      !error._isAuthEndpoint &&
      !error._sessionTimeoutHandled
    ) {
      console.log('Session timeout detected in response handler (401), triggering modal');
      triggerSessionTimeout();
    }
    
    // Handle network errors and exceptions thrown from callApi
    return {
      success: false,
      status: error.status || "Error",
      status_code: error.status_code || error.status || 0,
      message: error.message || 'An error occurred',
      request_id: error.request_id || null,
      data: error.data || null
    };
  }
};


/**
 * Response handler with toast notifications
 */
export const responseHandlerWithToast = async (response, successMessage = null) => {
  const result = await responseHandler(response);
  
  // Try to import toast dynamically
  try {
    const { toast } = await import('react-toastify');
    
    if (result.success) {
      const message = successMessage || result.message;
      if (message) { // Only show toast if there is a message
          toast.success(message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
      }
    } else {
      // Don't show toast for 401 errors since modal will handle it
      if (result.status_code !== 401) {
        toast.error(result.message, { // Use result.message which now contains the error message
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  } catch (e) {
    // Toast library not available, just return result
    console.log('Toast notification not available');
  }
  
  return result;
};


/**
 * Simple response handler (alias for backward compatibility)
 */
export const simpleResponseHandler = async (response) => {
  return await responseHandler(response);
};

export default responseHandler;
